/**
 * Backup Beacon, as drawn in R:\Projects\Backup Dashboard\brand.
 *
 * THE PRODUCT'S mark, not the tenant's. The sidebar already carries the
 * tenant's own logo through useLogo(); this is the thing that says which
 * software they are looking at, and it stays ours on every tenant and every
 * custom domain.
 *
 * INLINE SVG RATHER THAN AN IMPORTED FILE. A few hundred bytes, no request, and
 * it has to change with the theme - which an <img> cannot do without shipping
 * two files and picking between them anyway.
 *
 * TWO VARIANTS, NOT ONE RECOLOURED. From the brand kit: "The arcs get lighter
 * as they move outward in light mode and darker as they move outward in dark
 * mode. Do not flip this - the gradient of weight is what makes it read as
 * emanation rather than as a decorative swoosh."
 *
 * DUPLICATED IN admin/src/brand.tsx on purpose: web/ and admin/ are separate
 * Vite apps with no shared source root, and standing one up for two components
 * is a build change to solve a copy-paste problem. If the mark changes, change
 * both - and note that the theme hook each one imports is ALSO a separate copy,
 * which is how the admin console shipped a dark wordmark on a light page.
 */

import { useTheme } from './theme';

const NAVY = '#0B3A66';
const BLUE = '#2C7BD4';
const SKY = '#7FB4E8';
const MIST = '#DCEBFA';
const PAPER = '#E8F1FA';

const FONT = "Inter, 'Segoe UI', system-ui, -apple-system, sans-serif";

/** Mark only. 16px minimum, per the kit. */
export function Mark({ size = 40 }: { size?: number }) {
  const [theme] = useTheme();
  const dark = theme === 'dark';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" role="img" aria-label="Backup Beacon">
      <circle cx="10" cy="20" r="4.5" fill={dark ? MIST : NAVY} />
      <path d="M18 11a11 11 0 0 1 0 18" fill="none" stroke={dark ? SKY : BLUE} strokeWidth="3.6" strokeLinecap="round" />
      <path d="M25.5 6a17.5 17.5 0 0 1 0 28" fill="none" stroke={dark ? BLUE : SKY} strokeWidth="3.6" strokeLinecap="round" />
    </svg>
  );
}

/** Mark plus wordmark. 120px wide minimum, per the kit. */
export function Wordmark({ width = 200 }: { width?: number }) {
  const [theme] = useTheme();
  const dark = theme === 'dark';
  return (
    <svg
      width={width} height={width * (56 / 270)} viewBox="0 0 270 56"
      role="img" aria-label="Backup Beacon"
    >
      <g transform="translate(4, 8)">
        <circle cx="10" cy="20" r="4.5" fill={dark ? MIST : NAVY} />
        <path d="M18 11a11 11 0 0 1 0 18" fill="none" stroke={dark ? SKY : BLUE} strokeWidth="3.6" strokeLinecap="round" />
        <path d="M25.5 6a17.5 17.5 0 0 1 0 28" fill="none" stroke={dark ? BLUE : SKY} strokeWidth="3.6" strokeLinecap="round" />
      </g>
      <text
        x="52" y="38" fontFamily={FONT} fontSize="30" letterSpacing="-0.6"
        fill={dark ? PAPER : NAVY}
      >
        {/* One weight break, never all caps - the wordmark rule from the kit. */}
        <tspan fontWeight="400">Backup</tspan><tspan fontWeight="600">Beacon</tspan>
      </text>
    </svg>
  );
}

/**
 * The wordmark as a data URI, for the places that render an <img>.
 *
 * WHY BOTH FORMS EXIST. A tenant's own logo arrives as a data URI and is drawn
 * with <img src>. For the Backup Beacon default to flow through that same path
 * unchanged - same element, same sizing, same CSS - it has to be a data URI
 * too. A fallback that renders through a different code path is a fallback
 * nobody tests, and it stops matching the real case the first time either side
 * is styled.
 *
 * Percent-encoded rather than base64: it stays readable in devtools, and it is
 * shorter for SVG.
 */
function uri(svg: string): string {
  return 'data:image/svg+xml,' + encodeURIComponent(svg.replace(/\s+/g, ' ').trim());
}

const LOCKUP = (dot: string, inner: string, outer: string, ink: string) => `
<svg xmlns="http://www.w3.org/2000/svg" width="270" height="56" viewBox="0 0 270 56">
<g transform="translate(4, 8)">
<circle cx="10" cy="20" r="4.5" fill="${dot}"/>
<path d="M18 11a11 11 0 0 1 0 18" fill="none" stroke="${inner}" stroke-width="3.6" stroke-linecap="round"/>
<path d="M25.5 6a17.5 17.5 0 0 1 0 28" fill="none" stroke="${outer}" stroke-width="3.6" stroke-linecap="round"/>
</g>
<text x="52" y="38" font-family="Inter, 'Segoe UI', system-ui, -apple-system, sans-serif" font-size="30" letter-spacing="-0.6" fill="${ink}"><tspan font-weight="400">Backup</tspan><tspan font-weight="600">Beacon</tspan></text>
</svg>`;

/** For LIGHT backgrounds: arcs get lighter outward. */
export const BEACON_LIGHT = uri(LOCKUP(NAVY, BLUE, SKY, NAVY));
/** For DARK backgrounds: arcs get darker outward. Not the light one recoloured. */
export const BEACON_DARK = uri(LOCKUP(MIST, SKY, BLUE, PAPER));
